import React, { useState, useCallback, useMemo } from 'react';
import Apps from 'apps';
import { APP, BRAND_COLOR } from 'appenv';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Dialog, Slide } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import confirmationEmailResend from 'network/confirmationEmailResend';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextInput from 'components/input/TextInput';
import EmailIcon from 'components/icons/Email';
import { useTranslation, Trans } from 'react-i18next';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const StyledDialog = withStyles(() => ({
  paper: {
    borderRadius: '8px',
  },
  paperFullScreen: {
    borderRadius: '8px 8px 0 0',
  },
}))(Dialog);

const ModalContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  padding: 17px 16px 24px;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 960px) {
    width: 435px;
    min-height: 570px;
    padding: 16px 28px;
    align-items: flex-end;
  }
`;

const ResendButton = styled.button`
  width: 100%;
  border-radius: 4px;
  padding: 12px;
  font-size: 18px;
  line-height: 18px;
  margin-top: 24px;
  outline: none;
  color: #FFF;
  border: none;
  background-color: ${BRAND_COLOR || '#282B40'};
  transition: opacity 400ms;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 28px;
  color: #A2AAB3;
  cursor: pointer;

  @media only screen and (min-width: 960px) {
    margin-right: -10px;
  }
`;

const ReminderText = styled.div`
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #001246;
  margin: 24px 18px 26px;
`;

const EmailInput = styled(TextInput)`
  padding: 0;
  margin: 11px 0;

  input {
    font-size: 16px;
  }
`;

const ClickableText = styled.span`
  color: #5C8DF3;
  cursor: pointer;
  margin: 0 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const RegisterStickyContainer = styled.div`
  text-align: center;
  padding: 0 3px;
  padding-bottom: 24px;
  color: #282B40;
  margin-top: 4em;
  font-weight: bold;
  font-size: 1.2em;
  display: flex;
  flex-grow: 1;
  align-items: flex-end;
  justify-content: center;
`;

const ContactSupportContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 20px;
  padding: 0 15px;
  grid-gap: 5px;
  margin: 24px 0;
  white-space: break-spaces;
  color: #282B40;

  & path {
    fill: #282B40;
  }
`;

const StyledHelpOutlineIcon = withStyles(() => ({
  root: {
    height: 21,
    width: 21,
    marginRight: '5.5px',
  },
}))(HelpOutlineIcon);

const ContactSupportText = styled.span`
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #5C8DF3;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const PageSplitter = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const MessageOfResetContainer = styled.div`
  margin-top: 5px;
  color: red;
  font-style: italic;
  position: relative;
  top: 5px;
  text-align: center;
`;

const ReminderNoticeText = styled.span`
  font-weight: bold;
`;

const ModalTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} mountOnEnter unmountOnExit {...props} />
));

const validateEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const ContactSupport = ({ handleClose }) => {
  const { t } = useTranslation();

  const onCsLinkClick = useCallback(() => {
    handleClose();
    const { $zopim } = window;
    $zopim(() => {
      $zopim.livechat.window.show();
    });
  }, [handleClose]);

  return (
    <ContactSupportContainer>
      <StyledHelpOutlineIcon color={BRAND_COLOR} />
      {t('lobby.registration.need_help', 'Need help?')}
      <ClickableText
        onClick={onCsLinkClick}
      >
        <ContactSupportText>
          {t('contact_us', 'Contact us')}
        </ContactSupportText>
      </ClickableText>
    </ContactSupportContainer>
  );
};

ContactSupport.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

const EmailSentContainer = styled.div`
  text-align: center;
  color: #484848;
  fill: #484848;
  margin: 1.2em 16px 0;

  & > ${StyledTitle} {
    color: #484848;
  }

  & > ${ContactSupportContainer} {
    margin-top: 80px;
  }

  & > svg > path {
    fill: #DBDEE2;
  }
`;

const EmailSentContactSupportContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: flex-end;
`;

const EmailSent = ({ email, showEmailInput, handleClose }) => (
  <>
    <EmailSentContainer>
      <EmailIcon width={69} height={60} alt="Email Illustration" />
      <StyledTitle id="transition-modal-title">
        <Trans i18nKey="lobby.registration.check_mailbox" defaults="Check your mailbox" />
      </StyledTitle>
      <p>
        <Trans
          i18nKey="lobby.registration.sending_email"
          values={{ email }}
          components={{ b: <BoldText /> }}
          defaults="We are emailing you at <b>%{email}</b> with the access link."
        />
      </p>
      <p>
        <Trans
          i18nKey="lobby.registration.not_received_msg"
          values={{ minute: 10 }}
          components={{ b: <BoldText />, a: <ClickableText onClick={showEmailInput} /> }}
          defaults="If you have not received it in <b>%{minute} minutes</b>, please <a>try again</a>"
        />
      </p>
    </EmailSentContainer>
    <EmailSentContactSupportContainer>
      <ContactSupport handleClose={handleClose} />
    </EmailSentContactSupportContainer>
  </>
);

EmailSent.propTypes = {
  email: PropTypes.string.isRequired,
  showEmailInput: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
};

const ResetEmailModal = ({ open, handleClose, onRegisterNowBtnClick }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [isResent, setIsResent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [messageOfResent, setMessageOfResent] = useState('');
  const isMobile = useMediaQuery('(max-width: 960px)');
  const isEmailValid = useMemo(() => (email && email.trim() !== '' && validateEmail(email)), [email]);

  const setResentFalse = useCallback(() => setIsResent(false), [setIsResent]);

  const shouldEnableRegister = useMemo(() => (!['polyu618', 'sph706', Apps.Jetro817].includes(APP) && !isResent), [isResent]);

  const onEmailChange = useCallback(({ value }) => {
    setEmail(value);
  }, [setEmail]);

  const onModalExited = () => {
    setEmail('');
    setIsResent(false);
    setIsResending(false);
    setMessageOfResent(false);
  };

  const onResendClick = useCallback(async () => {
    setIsResending(true);
    const resendResult = await confirmationEmailResend(email.trim());
    if (resendResult.success) {
      setIsResent(true);
      setMessageOfResent('');
    } else {
      setMessageOfResent('Our server is currently busy, please try again later.');
    }
    setIsResending(false);
  }, [setIsResending, setIsResent, setMessageOfResent, email]);

  const onEmailInputKeyPress = useCallback((event) => {
    if (!isEmailValid) return;
    if (event.keyCode === 13) {
      onResendClick();
    }
  }, [onResendClick, isEmailValid]);

  return (
    <StyledDialog
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      onExited={onModalExited}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      TransitionComponent={ModalTransition}
      fullScreen={isMobile}
    >
      <ModalContainer>
        <StyledCloseIcon onClick={handleClose} />
        <PageSplitter>
          {!isResent && (
            <div>
              <StyledTitle id="transition-modal-title">
                {t('lobby.registration.title', 'Send me the access link')}
              </StyledTitle>
              <ReminderText>
                <Trans
                  i18nKey="lobby.registration.message"
                  components={{ b: <ReminderNoticeText /> }}
                  defaults="Enter your <b>registered<\/b> email. Then click the access link in your mailbox to log in seamlessly."
                />
              </ReminderText>
              <EmailInput
                autoFocus
                readOnly={isResending}
                defaultValue={email}
                label={t('lobby.registration.registered_email', 'Your registered email')}
                placeholder={t('lobby.registration.enter_email', 'Enter email')}
                sublabel={t('lobby.registration.registered_message', '*Only registered & approved users can receive the access link.*')}
                helperColor="#E2654D"
                onChange={onEmailChange}
                onKeyPress={onEmailInputKeyPress}
              />
              {messageOfResent && (
                <MessageOfResetContainer>
                  {messageOfResent}
                </MessageOfResetContainer>
              )}
              <ResendButton
                onClick={onResendClick}
                disabled={isResending || !isEmailValid}
              >
                {
                  isResending ? (
                    t('lobby.registration.sending', 'Sending')
                  ) : (
                    t('lobby.registration.send', 'Send')
                  )
                }
              </ResendButton>
              <ContactSupport handleClose={handleClose} />
            </div>
          )}
          {isResent && (<EmailSent email={email} showEmailInput={setResentFalse} handleClose={handleClose} />)}
          {shouldEnableRegister && (
            <RegisterStickyContainer>
              {`${t('lobby.registration.new_user', 'New user?')} `}
              <ClickableText
                onClick={onRegisterNowBtnClick}
              >
                {t('lobby.registration.register_now', 'Register now')}
              </ClickableText>
            </RegisterStickyContainer>
          )}
        </PageSplitter>
      </ModalContainer>
    </StyledDialog>
  );
};

ResetEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onRegisterNowBtnClick: PropTypes.func.isRequired,
};

export default ResetEmailModal;
